import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { T } from "../tokens";

const STORAGE_KEY = "fyz_admin_session";

/**
 * Snapshot the admin's own login before impersonating, so the "Stop" button
 * can restore it without forcing a re-login. Called from the AdminDashboard
 * impersonate flow.
 */
export function startImpersonationSession({ adminToken, adminUser, target, targetToken }) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      admin_token: adminToken,
      admin_user:  adminUser,
      target,
      started_at:  new Date().toISOString(),
    }),
  );
  // Swap the auth state to the target user.
  localStorage.setItem("fyz_token", targetToken);
  localStorage.setItem("fyz_user",  JSON.stringify(target));
}

export function readImpersonationSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearImpersonationSession() {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Always-visible bar at the top of the page when an impersonation session
 * is active. Two affordances:
 *   - "Stop" — restore the admin session and reload.
 *   - Click anywhere else on the bar — no-op (intentional, prevents accidents).
 */
export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [session, setSession] = useState(() => readImpersonationSession());

  // Re-check when the auth user changes (e.g., after login swap).
  useEffect(() => {
    setSession(readImpersonationSession());
  }, [user?.id]);

  if (!session) return null;

  const stop = () => {
    if (!session.admin_token || !session.admin_user) return;
    // Swap back to the admin's own session directly in localStorage
    localStorage.setItem("fyz_token", session.admin_token);
    localStorage.setItem("fyz_user", JSON.stringify(session.admin_user));
    clearImpersonationSession();
    // Hard reload directly to /admin to prevent any intermediate queries or race conditions.
    window.location.assign("/admin");
  };

  return (
    <>
      <style>{`
        body {
          margin-top: 44px !important;
        }
      `}</style>
      <div
        role="alert"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 44,
          boxSizing: "border-box",
          zIndex: 350,
          background: "linear-gradient(90deg, #2a1a08, #3a2410, #2a1a08)",
          borderBottom: `1px solid ${T.gold}`,
          color: T.goldLt,
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 18,
          fontSize: 13.5,
          fontFamily: "'DM Sans',sans-serif",
          letterSpacing: ".02em",
          boxShadow: "0 4px 12px rgba(0,0,0,.4)",
        }}
      >
      <span style={{ fontSize: 16 }}>👁</span>
      <span>
        Impersonating&nbsp;
        <strong style={{ color: T.text }}>{session.target?.name}</strong>
        &nbsp;({session.target?.email})
      </span>
      <button
        onClick={stop}
        style={{
          marginLeft: 8,
          padding: "5px 14px",
          borderRadius: 8,
          background: "transparent",
          border: `1px solid ${T.goldLt}`,
          color: T.goldLt,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "inherit",
          letterSpacing: ".04em",
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = T.goldLt; e.currentTarget.style.color = T.dark; }}
        onMouseOut={(e)  => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.goldLt; }}
      >
        Stop impersonating
      </button>
    </div>
    </>
  );
}
